import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  CheckCircle2,
  Clock3,
  KeyRound,
  LogOut,
  ShieldCheck,
  Trash2,
  UserRound,
} from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  updateDisplayNameInputSchema,
  updateTimeZoneInputSchema,
  type CalendarResponse,
  type SessionResponse,
  type SettingsResponse,
} from "@learn-flow/contracts";

import { AppShell, type SettingsRouteState } from "../../components/app-shell.js";
import { ApiError, apiRequest } from "../../lib/api.js";
import { sessionQueryKey } from "../auth/session.js";
import { calendarQueryKey, calendarQueryRoot } from "../calendar/calendar-api.js";
import { todayQueryRoot } from "../today/today-api.js";
import { ChangePasswordDialog } from "./change-password-dialog.js";
import { DeleteAccountDialog } from "./delete-account-dialog.js";
import { browserTimeZoneSuggestion, timeZoneLabel, timeZoneOptions } from "./time-zones.js";
import { loadSettings, saveDisplayName, saveTimeZone, settingsQueryKey } from "./settings-api.js";

export function SettingsPage(): React.JSX.Element {
  const settings = useQuery({
    queryKey: settingsQueryKey,
    queryFn: loadSettings,
  });

  useEffect(() => {
    document.title = "Settings · MemoKite";
  }, []);

  if (settings.isPending) {
    return (
      <AppShell mainClassName="settings-screen">
        <div className="settings-loading" aria-busy="true" aria-label="Loading settings">
          <span />
          <span />
          <span />
        </div>
      </AppShell>
    );
  }
  if (settings.isError) {
    return (
      <AppShell mainClassName="settings-screen">
        <section className="settings-state-card" role="alert">
          <h1>Settings could not be loaded</h1>
          <p>Your saved preferences have not changed.</p>
          <button type="button" className="soft-button" onClick={() => void settings.refetch()}>
            Try again
          </button>
        </section>
      </AppShell>
    );
  }

  return (
    <AppShell mainClassName="settings-screen">
      <LoadedSettings settings={settings.data} />
    </AppShell>
  );
}

function LoadedSettings({ settings }: { settings: SettingsResponse }): React.JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const routeState =
    location.state && typeof location.state === "object"
      ? (location.state as SettingsRouteState)
      : null;
  const initialReturnTo = safeReturnTo(routeState?.returnTo);
  const [returnTo, setReturnTo] = useState(initialReturnTo);
  const browserSuggestion = useMemo(browserTimeZoneSuggestion, []);
  const suggestedTimeZone = browserSuggestion ?? "UTC";
  const [displayName, setDisplayName] = useState(settings.displayName);
  const [displayNameFieldError, setDisplayNameFieldError] = useState("");
  const [displayNameSuccess, setDisplayNameSuccess] = useState("");
  const [displayNameSaveError, setDisplayNameSaveError] = useState("");
  const [timeZone, setTimeZone] = useState(settings.timeZone ?? suggestedTimeZone);
  const [fieldError, setFieldError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [saveError, setSaveError] = useState("");
  const [logoutError, setLogoutError] = useState("");
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const changePasswordTrigger = useRef<HTMLButtonElement>(null);
  const deleteAccountTrigger = useRef<HTMLButtonElement>(null);
  const shouldRestorePasswordFocus = useRef(false);
  const shouldRestoreDeleteFocus = useRef(false);

  const finishLogout = (): void => {
    queryClient.clear();
    queryClient.setQueryData(sessionQueryKey, null);
    void navigate("/login", { replace: true });
  };

  const displayNameSave = useMutation({
    mutationFn: (validatedDisplayName: string) =>
      saveDisplayName({ displayName: validatedDisplayName }),
    onSuccess: async (result) => {
      setDisplayName(result.displayName);
      setDisplayNameFieldError("");
      setDisplayNameSaveError("");
      setDisplayNameSuccess("Name saved. MemoKite will use it across your learning space.");
      queryClient.setQueryData(settingsQueryKey, result);
      queryClient.setQueryData<SessionResponse | null>(sessionQueryKey, (current) =>
        current ? { user: { ...current.user, displayName: result.displayName } } : current,
      );
      await queryClient.invalidateQueries({ queryKey: todayQueryRoot });
    },
    onError: (error) => {
      setDisplayNameSuccess("");
      if (error instanceof ApiError && error.status === 400) {
        setDisplayNameFieldError(
          error.problem?.fieldErrors?.displayName?.[0] ??
            "Choose the name you want MemoKite to use.",
        );
        setDisplayNameSaveError("");
        return;
      }
      setDisplayNameSaveError(
        `The name was not saved. MemoKite will keep calling you ${settings.displayName}.`,
      );
    },
  });

  const timeZoneSave = useMutation({
    mutationFn: (validatedTimeZone: string) => saveTimeZone({ timeZone: validatedTimeZone }),
    onSuccess: async (result) => {
      const nextReturnTo = rebaseCalendarReturn(returnTo, queryClient);
      setReturnTo(nextReturnTo);
      setFieldError("");
      setSaveError("");
      setSuccessMessage(
        "Timezone saved. Today, Calendar, and future session choices have been refreshed.",
      );
      queryClient.setQueryData(settingsQueryKey, result);
      queryClient.setQueryData<SessionResponse | null>(sessionQueryKey, (current) =>
        current ? { user: { ...current.user, timeZone: result.timeZone } } : current,
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: todayQueryRoot }),
        queryClient.invalidateQueries({ queryKey: calendarQueryRoot }),
        queryClient.invalidateQueries({ queryKey: ["session-planning-options"] }),
        queryClient.invalidateQueries({ queryKey: ["activity-detail"] }),
      ]);
    },
    onError: (error) => {
      setSuccessMessage("");
      if (error instanceof ApiError && error.status === 400) {
        setFieldError(
          error.problem?.fieldErrors?.timeZone?.[0] ?? "Choose a current IANA timezone.",
        );
        setSaveError("");
        return;
      }
      setSaveError(
        `The timezone was not saved. ${settings.timeZone ? `${timeZoneLabel(settings.timeZone)} remains active.` : "UTC fallback remains active."}`,
      );
    },
  });

  const logout = useMutation({
    mutationFn: () => apiRequest<void>("/api/v1/auth/logout", { method: "POST" }),
    onSuccess: finishLogout,
    onError: (error) => {
      if (error instanceof ApiError && error.status === 401) {
        finishLogout();
        return;
      }

      setLogoutError(
        "MemoKite could not log you out. Your session is still active; please try again.",
      );
    },
  });

  const validateAndSave = (): void => {
    const parsed = updateTimeZoneInputSchema.safeParse({ timeZone });
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? "Choose a current IANA timezone.");
      setSuccessMessage("");
      return;
    }
    setTimeZone(parsed.data.timeZone);
    setFieldError("");
    timeZoneSave.mutate(parsed.data.timeZone);
  };

  const validateAndSaveDisplayName = (): void => {
    const parsed = updateDisplayNameInputSchema.safeParse({ displayName });
    if (!parsed.success) {
      setDisplayNameFieldError(
        parsed.error.issues[0]?.message ?? "Choose the name you want MemoKite to use.",
      );
      setDisplayNameSuccess("");
      return;
    }
    setDisplayName(parsed.data.displayName);
    setDisplayNameFieldError("");
    displayNameSave.mutate(parsed.data.displayName);
  };

  const unchanged = settings.timeZoneStatus === "saved" && timeZone === settings.timeZone;
  const displayNameUnchanged = displayName === settings.displayName;
  useEffect(() => {
    if (passwordDialogOpen || !shouldRestorePasswordFocus.current) {
      return;
    }

    shouldRestorePasswordFocus.current = false;
    const frame = window.requestAnimationFrame(() => {
      changePasswordTrigger.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [passwordDialogOpen]);

  useEffect(() => {
    if (deleteDialogOpen || !shouldRestoreDeleteFocus.current) return;
    shouldRestoreDeleteFocus.current = false;
    const frame = window.requestAnimationFrame(() => deleteAccountTrigger.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [deleteDialogOpen]);

  const closePasswordDialog = (): void => {
    shouldRestorePasswordFocus.current = true;
    setPasswordDialogOpen(false);
  };
  const closeDeleteDialog = (): void => {
    shouldRestoreDeleteFocus.current = true;
    setDeleteDialogOpen(false);
  };
  return (
    <>
      <header className="settings-topbar">
        <div>
          <span className="section-kicker">Your learning space</span>
          <h1>Settings</h1>
        </div>
        <Link className="text-button" to={returnTo}>
          <ArrowLeft size={16} /> Back
        </Link>
      </header>

      <div className="settings-layout">
        <section className="settings-card display-name-card" aria-labelledby="display-name-heading">
          <div className="settings-card-heading">
            <span className="settings-card-icon" aria-hidden="true">
              <UserRound size={20} />
            </span>
            <div>
              <h2 id="display-name-heading">How should I call you?</h2>
              <p>This is the name MemoKite uses when it greets you.</p>
            </div>
          </div>

          <form
            className="timezone-form"
            onSubmit={(event) => {
              event.preventDefault();
              validateAndSaveDisplayName();
            }}
            noValidate
          >
            <label htmlFor="display-name">Name</label>
            <input
              id="display-name"
              value={displayName}
              autoComplete="name"
              maxLength={80}
              aria-invalid={displayNameFieldError ? "true" : undefined}
              aria-describedby="display-name-help display-name-error"
              disabled={displayNameSave.isPending}
              onChange={(event) => {
                setDisplayName(event.target.value);
                setDisplayNameFieldError("");
                setDisplayNameSaveError("");
                setDisplayNameSuccess("");
              }}
            />
            <p id="display-name-help" className="field-help">
              Choose any name you would like to see in your greeting.
            </p>
            <div id="display-name-error" className="auth-message" aria-live="polite">
              {displayNameFieldError ? (
                <p className="field-error" role="alert">
                  {displayNameFieldError}
                </p>
              ) : null}
              {displayNameSaveError ? (
                <p className="settings-inline-error" role="alert">
                  {displayNameSaveError}
                </p>
              ) : null}
            </div>
            <button
              type="submit"
              className="primary-button"
              disabled={displayNameSave.isPending || displayNameUnchanged}
            >
              {displayNameSave.isPending
                ? "Saving name…"
                : displayNameUnchanged
                  ? "Name saved"
                  : "Save name"}
            </button>
            <p className="settings-success" role="status" aria-live="polite">
              {displayNameSuccess ? (
                <>
                  <CheckCircle2 size={16} /> {displayNameSuccess}
                </>
              ) : null}
            </p>
          </form>
        </section>

        <section className="settings-card" aria-labelledby="timezone-heading">
          <div className="settings-card-heading">
            <span className="settings-card-icon" aria-hidden="true">
              <Clock3 size={20} />
            </span>
            <div>
              <h2 id="timezone-heading">Timezone</h2>
              <p>MemoKite uses this for Today, Calendar, and future session choices.</p>
            </div>
          </div>

          {settings.timeZoneStatus === "missing" ? (
            <div className="settings-notice">
              <strong>No timezone has been saved yet.</strong>
              <span>
                {browserSuggestion
                  ? `${timeZoneLabel(browserSuggestion)} is suggested from this browser. Save explicitly to use it.`
                  : "UTC is suggested until you explicitly save another timezone."}
              </span>
            </div>
          ) : settings.timeZoneStatus === "invalid" ? (
            <div className="settings-notice warning" role="alert">
              <strong>Your previous timezone is no longer supported.</strong>
              <span>UTC is protecting date calculations until you save a current timezone.</span>
            </div>
          ) : (
            <p className="settings-current">
              Currently saved: <strong>{timeZoneLabel(settings.timeZone ?? "UTC")}</strong>
            </p>
          )}

          <form
            className="timezone-form"
            onSubmit={(event) => {
              event.preventDefault();
              validateAndSave();
            }}
            noValidate
          >
            <label htmlFor="timezone">Search or enter an IANA timezone</label>
            <TimeZoneCombobox
              id="timezone"
              value={timeZone}
              disabled={timeZoneSave.isPending}
              invalid={Boolean(fieldError)}
              describedBy="timezone-help timezone-error"
              onValueChange={(value) => {
                setTimeZone(value);
                setFieldError("");
                setSaveError("");
                setSuccessMessage("");
              }}
            />
            <p id="timezone-help" className="field-help">
              Existing session days and times stay exactly as planned. Untimed sessions remain
              untimed.
            </p>
            <div id="timezone-error" className="auth-message" aria-live="polite">
              {fieldError ? (
                <p className="field-error" role="alert">
                  {fieldError}
                </p>
              ) : null}
              {saveError ? (
                <p className="settings-inline-error" role="alert">
                  {saveError}
                </p>
              ) : null}
            </div>
            <button
              type="submit"
              className="primary-button"
              disabled={timeZoneSave.isPending || unchanged}
            >
              {timeZoneSave.isPending
                ? "Saving timezone…"
                : unchanged
                  ? "Timezone saved"
                  : "Save timezone"}
            </button>
            <p className="settings-success" role="status" aria-live="polite">
              {successMessage ? (
                <>
                  <CheckCircle2 size={16} /> {successMessage}
                </>
              ) : null}
            </p>
          </form>
        </section>

        <section
          className="settings-card password-security-card"
          aria-labelledby="password-security-heading"
        >
          <div className="settings-card-heading">
            <span className="settings-card-icon" aria-hidden="true">
              <KeyRound size={20} />
            </span>
            <div>
              <h2 id="password-security-heading">
                {settings.hasPassword ? "Password security" : "Google sign-in"}
              </h2>
              <p>
                {settings.hasPassword
                  ? "Choose a new password and protect access to your learning space."
                  : "Your account is securely connected to your Google identity."}
              </p>
            </div>
          </div>
          <div className="password-security-copy">
            <strong>
              {settings.hasPassword ? "Keep your account secure" : "Connected with Google"}
            </strong>
            <p>
              {settings.hasPassword
                ? "Changing your password keeps this session active and securely logs out every other signed-in session."
                : "Use Continue with Google when you return to MemoKite. No MemoKite password is stored for this account."}
            </p>
          </div>
          {settings.hasPassword ? (
            <button
              ref={changePasswordTrigger}
              type="button"
              className="password-change-button"
              onClick={() => {
                setPasswordDialogOpen(true);
                setPasswordSuccess("");
              }}
            >
              <KeyRound size={17} aria-hidden="true" />
              Change password
            </button>
          ) : null}
          <p className="settings-success password-success" role="status" aria-live="polite">
            {passwordSuccess ? (
              <>
                <CheckCircle2 size={16} aria-hidden="true" /> {passwordSuccess}
              </>
            ) : null}
          </p>
        </section>

        <section className="settings-card account-card" aria-labelledby="account-heading">
          <div className="settings-card-heading">
            <span className="settings-card-icon" aria-hidden="true">
              <ShieldCheck size={20} />
            </span>
            <div>
              <h2 id="account-heading">Private account</h2>
              <p>{settings.email}</p>
            </div>
          </div>
          <div className="settings-logout-copy">
            <strong>Log out of MemoKite</strong>
            <p>
              This ends the session on this browser. Your library and learning plan stay safely
              stored.
            </p>
          </div>
          {logoutError ? (
            <div className="settings-inline-error" role="alert">
              <span>{logoutError}</span>
            </div>
          ) : null}
          <button
            type="button"
            className="logout-button"
            disabled={logout.isPending}
            onClick={() => {
              setLogoutError("");
              logout.mutate();
            }}
          >
            <LogOut size={17} />
            {logout.isPending ? "Logging out…" : logoutError ? "Try logging out again" : "Log out"}
          </button>
          <div className="settings-delete-account-copy">
            <strong>Delete your MemoKite account</strong>
            <p>
              Permanently remove your account, learning history, and uploaded files from live
              systems.
            </p>
          </div>
          <button
            ref={deleteAccountTrigger}
            type="button"
            className="delete-account-button"
            onClick={() => setDeleteDialogOpen(true)}
          >
            <Trash2 size={17} aria-hidden="true" /> Delete my account
          </button>
        </section>
      </div>
      {settings.hasPassword && passwordDialogOpen ? (
        <ChangePasswordDialog
          onDismiss={closePasswordDialog}
          onSessionInvalid={finishLogout}
          onChanged={() => {
            setPasswordSuccess(
              "Your password has been changed. Other signed-in sessions were securely logged out.",
            );
            closePasswordDialog();
          }}
        />
      ) : null}
      {deleteDialogOpen ? (
        <DeleteAccountDialog
          onDismiss={closeDeleteDialog}
          onDeleted={finishLogout}
          onSessionInvalid={finishLogout}
        />
      ) : null}
    </>
  );
}

type TimeZoneComboboxProps = {
  id: string;
  value: string;
  disabled: boolean;
  invalid: boolean;
  describedBy: string;
  onValueChange: (value: string) => void;
};

function TimeZoneCombobox({
  id,
  value,
  disabled,
  invalid,
  describedBy,
  onValueChange,
}: TimeZoneComboboxProps): React.JSX.Element {
  const listId = useId();
  const options = useMemo(() => {
    const query = value.trim().toLocaleLowerCase();
    return timeZoneOptions().filter(
      (option) =>
        !query ||
        option.value.toLocaleLowerCase().includes(query) ||
        option.label.toLocaleLowerCase().includes(query),
    );
  }, [value]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const activeOption = options[activeIndex];

  useEffect(() => {
    setActiveIndex(0);
  }, [value]);

  const selectOption = (nextValue: string): void => {
    onValueChange(nextValue);
    setOpen(false);
  };

  return (
    <div className="timezone-combobox">
      <input
        id={id}
        value={value}
        role="combobox"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open && activeOption ? `${listId}-option-${activeIndex}` : undefined}
        aria-invalid={invalid ? "true" : undefined}
        aria-describedby={describedBy}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          onValueChange(event.target.value);
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
            if (options.length > 0) {
              setActiveIndex((current) => Math.min(current + 1, options.length - 1));
            }
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setOpen(true);
            setActiveIndex((current) => Math.max(current - 1, 0));
          } else if (event.key === "Enter" && open && activeOption) {
            event.preventDefault();
            selectOption(activeOption.value);
          } else if (event.key === "Escape") {
            setOpen(false);
          }
        }}
        onBlur={() => setOpen(false)}
      />
      {open && options.length > 0 ? (
        <div id={listId} className="timezone-combobox-options" role="listbox">
          {options.map((option, index) => (
            <div
              id={`${listId}-option-${index}`}
              key={option.value}
              className="timezone-combobox-option"
              role="option"
              aria-selected={index === activeIndex}
              onPointerDown={(event) => {
                event.preventDefault();
                selectOption(option.value);
              }}
              onMouseEnter={() => setActiveIndex(index)}
            >
              <strong>{option.value}</strong>
              <span>{option.label}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function safeReturnTo(value: string | undefined): string {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return "/today";
  if (
    value === "/today" ||
    value.startsWith("/calendar") ||
    value.startsWith("/library") ||
    value.startsWith("/activities/")
  ) {
    return value;
  }
  return "/today";
}

function rebaseCalendarReturn(
  returnTo: string,
  queryClient: ReturnType<typeof useQueryClient>,
): string {
  const url = new URL(returnTo, "https://learnflow.local");
  if (url.pathname !== "/calendar") return returnTo;
  const startDate = url.searchParams.get("start");
  if (!startDate) return "/calendar";
  const cached = queryClient.getQueryData<CalendarResponse>(calendarQueryKey(startDate));
  return cached?.range.startDate === cached?.range.currentDate ? "/calendar" : returnTo;
}
